/**
 * OrbitalItem
 *
 * 궤도 위 아이템 컴포넌트 (마커 + 텍스트)
 * - 원형 마커와 텍스트 라벨
 * - 펄스 애니메이션 + 페이드 인/아웃
 * - 각도 기반 텍스트 정렬
 */

import React from 'react';
import { motion } from 'framer-motion';
import { getTextAnchor, getTextOffset, truncateText } from '../../../hooks/useOrbitalConfig';

/**
 * OrbitalItem
 *
 * @param {number} x - X 좌표
 * @param {number} y - Y 좌표
 * @param {number} angle - 각도 (텍스트 정렬용)
 * @param {string} text - 표시할 텍스트
 * @param {number} fontSize - 폰트 크기
 * @param {number} markerSize - 마커 크기
 * @param {string} markerColor - 마커 색상
 * @param {string} textColor - 텍스트 색상
 * @param {number} maxTextLength - 최대 텍스트 길이
 * @param {number} animationDelay - 애니메이션 지연
 * @param {boolean} showMarker - 마커 표시 여부
 * @param {'thread' | 'friend'} type - 아이템 타입
 */
const OrbitalItem = ({
  x,
  y,
  angle,
  text,
  fontSize = 12,
  markerSize = 8,
  markerColor = 'currentColor',
  textColor = 'currentColor',
  maxTextLength = 25,
  animationDelay = 0,
  showMarker = true,
  type = 'thread',
}) => {
  const textAnchor = getTextAnchor(angle);
  const textOffset = getTextOffset(angle, markerSize + 4);
  const displayText = truncateText(text, maxTextLength);

  // 마커 펄스 애니메이션
  const markerAnimation = {
    scale: [1, 1.3, 1],
    opacity: [0.7, 1, 0.7],
  };

  const markerTransition = {
    duration: 2,
    repeat: Infinity,
    ease: 'easeInOut',
    delay: animationDelay,
  };

  // 아이템 페이드 인/아웃 애니메이션
  const itemAnimation = {
    opacity: [0, 0.9, 0.9, 0],
    scale: [0.8, 1, 1, 0.9],
  };

  const itemTransition = {
    duration: 8,
    repeat: Infinity,
    repeatDelay: 2,
    ease: 'easeInOut',
    delay: animationDelay,
  };

  // 친구 타입일 때 @ 접두어 추가
  const formattedText = type === 'friend' && text && !text.startsWith('@')
    ? `@${displayText}`
    : displayText;

  return (
    <motion.g
      initial={{ opacity: 0, scale: 0.8 }}
      animate={itemAnimation}
      transition={itemTransition}
    >
      {/* 마커 (원형) */}
      {showMarker && (
        <motion.circle
          cx={x}
          cy={y}
          r={markerSize / 2}
          fill={markerColor}
          animate={markerAnimation}
          transition={markerTransition}
        />
      )}

      {/* 텍스트 라벨 */}
      <text
        x={x + textOffset.dx}
        y={y + textOffset.dy}
        textAnchor={textAnchor}
        dominantBaseline="middle"
        fontSize={fontSize}
        fill={textColor}
        style={{
          fontFamily: '"source-han-serif-korean", "Noto Serif KR", serif',
          fontWeight: type === 'friend' ? 600 : 500,
        }}
      >
        {formattedText}
      </text>
    </motion.g>
  );
};

export default OrbitalItem;
